import React from "react";
import { useTranslation } from "react-i18next";
import { PaymentMethodSummary } from "../../api/services";

interface Props {
  methods: PaymentMethodSummary[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  emptyMessage?: string;
  /**
   * The only method type selectable, per the payment-timing rule:
   * `"card"` when coverage starts within CARD_ONLY_THRESHOLD_DAYS (ACH
   * wouldn't clear in time), `"bank"` when it starts later, `null`/omitted
   * for no restriction. Methods of the other type render disabled.
   */
  allowedType?: "card" | "bank" | null;
  /**
   * Hint rendered under disabled rows explaining why they can't be picked.
   * Pass a translation string from the caller.
   */
  disabledHint?: string;
}

const brandLabel = (brand: string | null, fallback: string) =>
  brand
    ? brand
        .split(/[_\s]/)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(" ")
    : fallback;

/**
 * Single-select picker over the user's saved payment methods.
 * Used by SDA purchase and claim repayment screens so both flows render
 * identical rows and share keyboard/accessibility behavior.
 */
export const PaymentMethodPicker: React.FC<Props> = ({
  methods,
  selectedId,
  onSelect,
  emptyMessage,
  allowedType = null,
  disabledHint,
}) => {
  const { t } = useTranslation();
  if (methods.length === 0) {
    return (
      <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-warning">
        {emptyMessage || t("sda.payment.emptyMethods")}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {methods.map((m) => {
        const isSelected = selectedId === m.id;
        const isCard = m.type === "card";
        const isDisabled =
          allowedType !== null &&
          (allowedType === "card" ? !isCard : isCard);
        return (
          <div key={m.id}>
            <label
              className={`flex items-center gap-3 rounded-lg border-2 px-4 py-3 transition-all ${
                isDisabled
                  ? "cursor-not-allowed border-gray-200 bg-gray-50 opacity-60"
                  : isSelected
                    ? "cursor-pointer border-primary bg-primary/5"
                    : "cursor-pointer border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20 disabled:cursor-not-allowed"
                name="paymentMethodPicker"
                checked={isSelected && !isDisabled}
                disabled={isDisabled}
                onChange={() => {
                  if (!isDisabled) onSelect(m.id);
                }}
              />
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                isCard
                  ? "bg-primary/10 text-primary"
                  : "bg-green-50 text-success"
              }`}
            >
              {isCard ? (
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v4M12 14v4M16 14v4"
                  />
                </svg>
              )}
            </span>
              <div className="grow">
                <p className="text-sm font-semibold text-primary">
                  {isCard
                    ? brandLabel(m.brand, t("paymentMethodPicker.card"))
                    : m.bankName || t("paymentMethodPicker.bankAccount")}
                  {m.isDefault && (
                    <span className="ml-2 rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-success">
                      {t("paymentMethodPicker.default")}
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted">
                  {isCard ? "•••• •••• •••• " : "••••"}
                  {m.last4}
                  {isCard && m.expMonth && m.expYear && (
                    <span className="ml-2">
                      {t("paymentMethodPicker.exp", {
                        month: String(m.expMonth).padStart(2, "0"),
                        year: String(m.expYear).slice(-2),
                      })}
                    </span>
                  )}
                </p>
              </div>
            </label>
            {isDisabled && disabledHint && (
              <p className="ml-7 mt-1 text-xs text-warning">{disabledHint}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PaymentMethodPicker;
